import {useRef, useState} from 'react';
import axios from 'axios';
import {useDispatch} from 'react-redux';
import {QUIKLEARN_BACKEND_URL} from 'config/app.config';
import {showQuotaPaywall} from 'store/subscriptionUi';
import {getWebAccessToken} from 'utils/webAuthSession';
import {buildTutorTokenRequest} from 'utils/tutorContext';
import {tutorPurpose} from 'firstVisit/events';
import {tutorRefusalPaywall} from 'utils/billing/billingModel';

const useFetchTokenLiveKit = () => {
  const dispatch = useDispatch();
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // The server granted the guided first visit's free tour question for this session (Sol onboarding-build-2 D7 B).
  const [firstVisitFree, setFirstVisitFree] = useState(false);
  // The plan's own minutes on this mode when the free question started. The tutor's meter adds the free session's
  // 90 seconds to it ("3 min left" on the 2-minute Free plan, owner 2026-10-10), so the bar shows this instead.
  const [planSecondsLeft, setPlanSecondsLeft] = useState(null);
  const quotaBlockedRef = useRef(false);

  /**
   * Fetch a LiveKit token.
   *
   * IMPORTANT: This function is intentionally **non-throwing**. It returns both the token and
   * the structured failure so its caller never has to wait for React state to rerender.
   *
   * Reason: historically, callers didn't consistently `.catch()` token fetch
   * failures, which can surface as disruptive overlays/unhandled promise
   * rejections in the web app when the token service is down.
   */
  const getToken = async request => {
    setLoading(true);
    quotaBlockedRef.current = false;
    try {
      const accessToken = getWebAccessToken();
      if (!accessToken) throw new Error('Your web learning session has expired. Please reopen the course.');
      const response = await axios.post(
        `${QUIKLEARN_BACKEND_URL}/api/user/tutor-sessions/token`,
        buildTutorTokenRequest({...request, purpose: tutorPurpose()}),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const nextToken = response?.data?.data?.token || response?.data?.token || null;
      setFirstVisitFree(response?.data?.data?.firstVisitFree === true);
      const planLeft = Number(response?.data?.data?.remainingSeconds);
      setPlanSecondsLeft(Number.isFinite(planLeft) ? planLeft : null);
      setToken(nextToken);
      setError(null);
      return {token: nextToken, error: null};
    } catch (err) {
      // A unit outside the plan opens the locked-unit plans; a used-up meter, the quota plans
      // (Sol confirm-free-grade #1).
      const paywall = tutorRefusalPaywall(err?.response?.status, err?.response?.data, request?.mode);
      if (paywall) {
        quotaBlockedRef.current = true;
        dispatch(showQuotaPaywall(paywall));
      }
      console.error('Tutor token request failed', {status: err?.response?.status || null});
      const failure = {
        code: err?.response?.data?.code || null,
        message: err?.response?.data?.message || err?.message || 'An error occurred',
      };
      setError(failure);
      setFirstVisitFree(false);
      setPlanSecondsLeft(null);
      setToken(null);
      return {token: null, error: failure};
    } finally {
      setLoading(false);
    }
  };

  const clearToken = () => {
    setToken(null);
    setError(null);
    setFirstVisitFree(false);
    setPlanSecondsLeft(null);
  };

  return {
    token,
    error,
    loading,
    firstVisitFree,
    planSecondsLeft,
    getToken,
    clearToken,
    wasQuotaBlocked: () => quotaBlockedRef.current,
  };
};

export default useFetchTokenLiveKit;
